import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

type ImagePreviewCardProps = {
  title: string;
  value: string; // path or url
  resolveSrc: (value: string) => string;
  onDelete?: () => void;
  size?: number; // square size in px
  defaultImage?: string;
};

const ImagePreviewCard: React.FC<ImagePreviewCardProps> = ({
  title,
  value,
  resolveSrc,
  onDelete,
  size = 200,
  defaultImage,
}) => {
  const src = resolveSrc(value);

  const handleDownload = async () => {
    try {
      const response = await fetch(src, { mode: 'cors' });
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const rawName = (value || 'image').toString().split('/').pop() || 'image';
      const extFromType = blob.type && blob.type.includes('/') ? `.${blob.type.split('/')[1]}` : '';
      link.download = rawName.includes('.') ? rawName : `${rawName}${extFromType || '.png'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      window.open(src, '_blank');
    }
  };

  const [modalOpen, setModalOpen] = React.useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" color="textSecondary">
        {title}
      </Typography>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fafafa',
          position: 'relative',
          cursor: 'pointer',
          '&:hover .image-actions': { opacity: 1 },
          '&:hover .image-dim': { opacity: 1 },
        }}
        onClick={() => setModalOpen(true)}
      >
        <Box className="image-dim" sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', opacity: 0, transition: 'opacity 0.2s ease-in-out', pointerEvents: 'none', zIndex: 1 }} />
        <Box className="image-actions" sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1, zIndex: 2, opacity: 0, transition: 'opacity 0.2s ease-in-out' }}>
          <Box component="button" onClick={(e) => { e.stopPropagation(); handleDownload(); }} title="Download" style={{ border: 'none', background: 'rgba(33, 150, 243, 0.9)', color: '#fff', width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            <DownloadIcon sx={{ fontSize: 18, color: 'white' }} />
          </Box>
          {onDelete && (
            <Box component="button" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete" style={{ border: 'none', background: 'rgba(229, 57, 53, 0.9)', color: '#fff', width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
              <DeleteOutlineIcon sx={{ fontSize: 18, color: 'white' }} />
            </Box>
          )}
        </Box>
        <img
          src={src}
          alt={title}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { if (defaultImage) (e.target as HTMLImageElement).src = defaultImage; }}
        />
      </Box>
      {modalOpen && (
        <Box onClick={() => setModalOpen(false)} sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Box sx={{ backgroundColor: '#000', borderRadius: 1, maxWidth: '90vw', maxHeight: '90vh', p: 1, position: 'relative' }}>
            <IconButton
              onClick={(e) => { e.stopPropagation(); setModalOpen(false); }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            <img src={src} alt={title} style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ImagePreviewCard;


